import PropTypes from 'prop-types';
import { Box, Chip, IconButton, ListItemButton, Tooltip, Typography, alpha, useTheme } from '@mui/material';
import { Icon } from '@iconify/react';
import {
    WA_GREEN,
    WA_LIGHT_GREEN,
    getUsageTypeColor,
    formatUsageType,
    formatTemplateName,
    stripToPlain,
    getTemplateBody,
} from './whatsAppTemplateUtils';

// ----------------------------------------------------------------------
// Renders a single template row in the left-panel list of WhatsAppTemplateSelector.
// Also usable in WhatsappTemplateList admin table for visual consistency.
// ----------------------------------------------------------------------

// Map raw Meta status to a display bucket.
// Matches the norm() logic in SearchLeadForWhatsapp.jsx lines 160-163.
export const normTemplateStatus = (template) => {
    const s = String(template?.status || '').toUpperCase();
    if (s === 'APPROVED') return 'APPROVED';
    if (s === 'PENDING' || s === 'IN_APPEAL' || s === 'PENDING_DELETION') return 'PENDING';
    if (s === 'REJECTED' || s === 'DISABLED' || s === 'PAUSED') return 'REJECTED';
    return null; // unknown / no status field → treat as sendable
};

const STATUS_CHIP = {
    PENDING: { label: 'Pending', bg: 'rgba(245,158,11,0.12)', color: '#b45309', icon: 'mdi:clock-outline' },
    REJECTED: { label: 'Rejected', bg: 'rgba(239,68,68,0.12)', color: '#dc2626', icon: 'mdi:alert-circle-outline' },
};

export default function WhatsAppTemplateListItem({
    template, isSelected, onClick, onToggleFavourite,
    // Audience state for the CURRENT recipient, resolved by the caller.
    // `audienceBlockedReason` — non-empty means this template may not be sent to
    // this patient; rendered exactly like the existing Meta "Not sendable" chip
    // so both kinds of un-sendability read the same way.
    // `audienceSummary` — short label of who the template reaches at all
    // ("No NHS", "2 treatments blocked"), shown whenever the template is
    // restricted so nobody has to open it to find out.
    audienceBlockedReason, audienceSummary,
}) {
    const theme = useTheme();
    const typeColor = getUsageTypeColor(template.usage_type, theme);
    const bodyPreview = stripToPlain(getTemplateBody(template));
    const statusBucket = normTemplateStatus(template);
    const statusChip = STATUS_CHIP[statusBucket];
    const favourite = Boolean(template.is_favourite);

    const handleFavouriteClick = (e) => {
        e.stopPropagation(); // don't also select the template
        onToggleFavourite?.(template);
    };

    return (
        <ListItemButton
            selected={isSelected}
            onClick={onClick}
            sx={{
                py: 1.25,
                px: 2,
                borderBottom: `1px solid ${theme.palette.divider}`,
                borderLeft: isSelected
                    ? `4px solid ${WA_LIGHT_GREEN}`
                    : '4px solid transparent',
                bgcolor: isSelected
                    ? alpha(WA_LIGHT_GREEN, 0.1)
                    : 'transparent',
                boxShadow: isSelected ? `inset 0 0 0 1px ${alpha(WA_LIGHT_GREEN, 0.18)}` : 'none',
                '&:hover': {
                    bgcolor: isSelected
                        ? alpha(WA_LIGHT_GREEN, 0.14)
                        : theme.palette.grey[50],
                },
                transition: 'all 0.15s ease',
                pr: 1, // tighter right pad to fit star
            }}
        >
            <Box sx={{ flex: 1, minWidth: 0 }}>
                {/* Row 1: name + usage chip */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.25 }}>
                    <Typography
                        sx={{
                            fontWeight: 600,
                            fontSize: '0.82rem',
                            color: 'text.primary',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            flex: 1,
                        }}
                    >
                        {formatTemplateName(template.name)}
                    </Typography>
                    {isSelected && (
                        <Box
                            component="span"
                            sx={{
                                width: 18,
                                height: 18,
                                borderRadius: '50%',
                                display: 'inline-flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                flexShrink: 0,
                                bgcolor: WA_LIGHT_GREEN,
                                color: '#fff',
                            }}
                        >
                            <Icon icon="mdi:check" width={13} />
                        </Box>
                    )}
                    <Chip
                        label={formatUsageType(template.usage_type)}
                        size="small"
                        sx={{
                            height: 18,
                            fontSize: '0.6rem',
                            fontWeight: 600,
                            flexShrink: 0,
                            bgcolor: isSelected
                                ? alpha(WA_LIGHT_GREEN, 0.15)
                                : typeColor.bg,
                            color: isSelected
                                ? WA_GREEN
                                : typeColor.color,
                            '& .MuiChip-label': { px: 0.75 },
                        }}
                    />
                </Box>

                {/* Row 2: body preview */}
                {bodyPreview && (
                    <Typography
                        sx={{
                            fontSize: '0.72rem',
                            color: 'text.secondary',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            lineHeight: 1.4,
                        }}
                    >
                        {bodyPreview}
                    </Typography>
                )}

                {/* Row 3: status affordance for non-APPROVED templates */}
                {statusChip && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
                        <Icon icon={statusChip.icon} width={11} style={{ color: statusChip.color, flexShrink: 0 }} />
                        <Typography sx={{ fontSize: '0.66rem', color: statusChip.color, fontWeight: 500 }}>
                            {statusBucket === 'PENDING' ? 'Awaiting Meta approval' : (template.status_reason || 'Not sendable')}
                        </Typography>
                    </Box>
                )}

                {/* Audience state, in the row rather than after a failed send.
                    Blocked reads red like the Meta chip above; merely-restricted
                    is a quiet grey note. Either way the ⓘ carries the full
                    sentence on hover. */}
                {(audienceBlockedReason || audienceSummary) && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5, minWidth: 0 }}>
                        <Icon
                            // Same icon and red as STATUS_CHIP.REJECTED above, so
                            // "cannot be sent" looks identical whether the cause is
                            // Meta or an audience rule.
                            icon={audienceBlockedReason ? 'mdi:alert-circle-outline' : 'mdi:account-filter-outline'}
                            width={11}
                            style={{ color: audienceBlockedReason ? '#dc2626' : '#919EAB', flexShrink: 0 }}
                        />
                        <Typography
                            noWrap
                            sx={{
                                fontSize: '0.66rem',
                                fontWeight: audienceBlockedReason ? 500 : 400,
                                color: audienceBlockedReason ? '#dc2626' : 'text.disabled',
                                minWidth: 0,
                            }}
                        >
                            {/* Blocked shows the SAME rule text, in red — the rule is
                                what the operator recognises ("Excludes 3 treatments"),
                                and red is what tells them it applies to this lead.
                                Falls back when there is no summary to show. */}
                            {audienceBlockedReason
                                ? (audienceSummary || 'Not sendable to this lead')
                                : audienceSummary}
                        </Typography>
                        <Tooltip title={audienceBlockedReason || audienceSummary} placement="top">
                            <Box
                                component="span"
                                sx={{ display: 'inline-flex', flexShrink: 0, cursor: 'help' }}
                                onClick={(e) => e.stopPropagation()}
                            >
                                <Icon
                                    icon="mdi:information-outline"
                                    width={11}
                                    style={{ color: audienceBlockedReason ? '#dc2626' : '#919EAB' }}
                                />
                            </Box>
                        </Tooltip>
                    </Box>
                )}
            </Box>

            {/* Star / favourite toggle */}
            {onToggleFavourite && (
                <Tooltip
                    title={favourite ? 'Remove from favourites' : 'Add to favourites'}
                    placement="right"
                    arrow
                >
                    <IconButton
                        className="favourite-btn"
                        size="small"
                        onClick={handleFavouriteClick}
                        sx={{
                            ml: 0.5,
                            flexShrink: 0,
                            width: 28,
                            height: 28,
                            opacity: 1,
                            color: favourite ? '#f59e0b' : 'text.disabled',
                            bgcolor: favourite ? 'rgba(245,158,11,0.12)' : 'transparent',
                            transform: favourite ? 'scale(1.08)' : 'scale(1)',
                            transition: 'color 0.15s, background-color 0.15s, transform 0.15s',
                            animation: favourite ? 'templateFavoritePop 180ms ease-out' : 'none',
                            '@keyframes templateFavoritePop': {
                                '0%': { transform: 'scale(0.82)' },
                                '70%': { transform: 'scale(1.16)' },
                                '100%': { transform: 'scale(1.08)' },
                            },
                            '&:hover': {
                                bgcolor: 'rgba(245,158,11,0.1)',
                                color: '#f59e0b',
                            },
                        }}
                    >
                        <Icon
                            icon={favourite ? 'mdi:star' : 'mdi:star-outline'}
                            width={16}
                        />
                    </IconButton>
                </Tooltip>
            )}
        </ListItemButton>
    );
}

WhatsAppTemplateListItem.propTypes = {
    template: PropTypes.object.isRequired,
    isSelected: PropTypes.bool,
    onClick: PropTypes.func,
    onToggleFavourite: PropTypes.func,
    audienceBlockedReason: PropTypes.string,
    audienceSummary: PropTypes.string,
};

WhatsAppTemplateListItem.defaultProps = {
    isSelected: false,
    onClick: () => {},
    onToggleFavourite: undefined,
    audienceBlockedReason: '',
    audienceSummary: '',
};
